
import { mapState } from 'pinia';
import { ValidationObserver } from 'vee-validate';
import FormMixin from '@Mixins/form';
import { buildSrc, buildFixedSrcSet } from '@Utils/images';
import { FIELD_NAMES } from '@Settings/forms.settings';
import { useUser } from '@Stores/user';

const types = ['success', 'error'];

export default {
  name: 'EligibilityResult',
  components: {
    ValidationObserver,
  },
  mixins: [FormMixin],
  props: {
    /**
     * Tested address
     */
    address: {
      type: String,
      required: true,
    },
    /**
     * Label of cancel CTA
     */
    cancel: {
      type: String,
      default: undefined,
    },
    /**
     * CTA label
     */
    ctaLabel: {
      type: String,
      default: undefined,
    },
    /**
     * Whether dumb img is display
     */
    dumbImg: {
      type: Boolean,
      default: false,
    },
    /**
     * Footer text
     */
    footer: {
      type: String,
      default: undefined,
    },
    /**
     * Push Image
     * @property {string} alt Image alt
     * @property {string} src Image src
     * @property {string} type Image type (dam or static)
     */
    img: {
      type: [Object, String],
      default: undefined,
    },
    /**
     * Loading state
     */
    loaders: {
      type: Object,
      required: true,
    },
    /**
     * Secondary CTA label
     */
    secondaryCtaLabel: {
      type: String,
      default: undefined,
    },
    /**
     * Type of result
     */
    type: {
      type: String,
      default: types[0],
      validator: (type) => types.includes(type),
    },
    /**
     * Result text
     */
    text: {
      type: String,
      default: undefined,
    },
    /**
     * Result title
     */
    title: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      checkbox: {
        input: {
          id: 'eligibility-checkbox',
          name: 'eligibility-checkbox',
          value: false,
        },
        label: this.$t('forms.labels.eligibilityContactCheckbox'),
        type: 'checkbox',
      },
      checkboxValue: false,
      email: {
        autocomplete: 'email',
        id: FIELD_NAMES.email,
        label: this.$t('forms.labels.email'),
        name: FIELD_NAMES.email,
        rules: {
          email: true,
          required: true,
        },
        size: 'l',
        type: 'email',
        validationName: 'email',
      },
      emailValue: undefined,
      reset: {
        label: this.$t('delivery.eligibilityResult.reset'),
        size: 'xs',
        variant: 2,
      },
    };
  },
  computed: {
    ...mapState(useUser, {
      loggedIn: 'loggedIn',
    }),
    cssClass() {
      return [
        `-type-${this.type}`,
        {
          '-has-footer': this.footer,
        },
      ];
    },
    displayEmailInput() {
      return this.checkboxValue && !this.loggedIn;
    },
    eligibilityData() {
      return {
        checkboxValue: this.checkboxValue,
        email: this.emailValue,
      };
    },
    eventName() {
      return this.isEligible ? 'click' : null;
    },
    imgAttributes() {
      if (!this.img) {
        return undefined;
      }

      return {
        alt: this.img.alt || '',
        loading: 'lazy',
        src: buildSrc({
          img: this.img,
          width: 600,
        }),
        srcset: buildFixedSrcSet({
          img: this.img,
          width: 600,
        }),
        width: '600',
      };
    },
    isEligible() {
      return this.type === types[0];
    },
    mainCta() {
      if (!this.ctaLabel) {
        return undefined;
      }

      let isMainCtaLoading;
      if (this.isEligible || !this.checkboxValue) {
        isMainCtaLoading = this.loaders.loadingDeliveryAddress;
      }
      if (!this.isEligible && this.checkboxValue) {
        isMainCtaLoading =
          this.loaders.loadingDeliveryAddress &&
          this.loaders.loadingNotificationAddress;
      }
      const mainCtaProps = {
        label: this.ctaLabel,
        loading: isMainCtaLoading,
        size: 'xl',
        variant: 3,
      };
      if (!this.isEligible) {
        mainCtaProps.attributes = {
          form: 'eligibility-result-form',
          type: 'submit',
        };
      }
      return mainCtaProps;
    },
    secondaryCta() {
      if (!this.secondaryCtaLabel) {
        return undefined;
      }

      const isSecondaryCtaLoading =
        this.loaders.loadingNotificationAddress &&
        !this.loaders.loadingDeliveryAddress;

      return {
        label: this.secondaryCtaLabel,
        layout: 'solid',
        loading: isSecondaryCtaLoading,
        size: 'xl',
        variant: 4,
      };
    },
  },
  methods: {
    onPrimaryClick() {
      this.$emit('validate', this.eligibilityData);
    },
    onSecondaryClick() {
      const eligibilityData = !this.isEligible ? this.eligibilityData : false;
      this.$emit('cancel', false, eligibilityData);
    },
  },
};
